<template>
  <div
    v-for="seat in getLegendSeats"
    :key="seat.name + seat.customSeatTypeColor"
    :class=" {
      'col-auto row no-wrap justify-center top-legend': topLegend,
      'row justify-between items-center': !topLegend,
      'column': vertical
    }"
    data-test-id="legend-seats-item"
  >
    <div
      :style="{
        'max-width': topLegend ? '22px' : '35px',
        'min-width': topLegend ? '22px' : '35px'
      }"
      class="seat"
    >
      <div
        class="seat-content"
        :class="[seat.class, seat.seatType]"
        :style="seat.customSeatTypeColor ? `color: ${seat.customSeatTypeColor} !important;` : ''"
      >
        <q-icon
          v-if="showSeatIcon(seat)"
          :class=" {
            'absolute selected-icon': seat.name === AVAILABILITY_TYPES.SELECTED,
            'absolute-center': seat.name !== AVAILABILITY_TYPES.SELECTED,
          }"
          :style="{
            top: seat.name === AVAILABILITY_TYPES.SELECTED ? '1%' : undefined,
            'padding-bottom': seat.name !== AVAILABILITY_TYPES.SELECTED ? '5px' : undefined
          }"
          :color="seatIconColor(seat)"
          :name="seatIcon(seat)"
          :size="topLegend ? '8px' : '12px'"
        />
        <svg viewBox="0 0 220 220">
          <use :xlink:href="'/seats.svg#' + seat.seatType" />
        </svg>
        <div
          v-if="seat.name === AVAILABILITY_TYPES.SELECTED"
          class="row justify-center q-pt-xs"
        >
          <div
            class="seat-text text-responsive"
            :style="{ top: vertical || !topLegend ? '25%' : '15%' }"
            style="font-size: 10px;"
          >
            A1
          </div>
        </div>
      </div>
    </div>
    <div class="text-caption q-mt-xs q-ml-xs top-legend-seat-name text-responsive">
      {{ seat.label || seat.name }}
    </div>
  </div>
</template>

<script>
import i18n from 'src/i18n'
import { AVAILABILITY_TYPES } from 'src/models/screen/SeatChart'

const legendSeats = [
  {
    name: AVAILABILITY_TYPES.AVAILABLE,
    label: i18n.global.t('status.available'),
    seatType: 'regular-seat',
    required: true
  },
  {
    name: AVAILABILITY_TYPES.SELECTED,
    label: i18n.global.t('actions.selected'),
    seatType: 'regular-seat',
    class: 'selected',
    required: true
  },
  {
    name: AVAILABILITY_TYPES.RESERVED,
    label: i18n.global.t('status.reserved'),
    seatType: 'regular-seat',
    class: 'reserved',
    required: true
  },
  {
    name: AVAILABILITY_TYPES.UNAVAILABLE,
    label: i18n.global.t('status.unavailable'),
    seatType: 'regular-seat',
    class: 'unavailable',
    required: true
  },
  {
    name: AVAILABILITY_TYPES.SOCIAL_DISTANCING,
    label: i18n.global.t('seatChart.socialDistancing'),
    seatType: 'regular-seat',
    class: 'reserved social-distancing'
  },
  {
    name: AVAILABILITY_TYPES.PRIOR_SEATS,
    label: i18n.global.t('seatChart.priorSeats'),
    seatType: 'regular-seat',
    class: 'previous-selection',
    topLegend: true
  },
  {
    name: i18n.global.t('seatChart.types.recliner'),
    seatType: 'recliner-seat'
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: 'couch-center'
  },
  {
    name: i18n.global.t('seatChart.types.wheelchair'),
    seatType: 'wheelchair'
  },
  {
    name: i18n.global.t('seatChart.types.table'),
    seatType: 'table'
  }
]
export default {
  name: 'LegendSeatsItem',
  configuration: ['currentSite'],
  props: {
    topLegend: {
      type: Boolean,
      required: false,
      default: false
    },
    seatChartArray: {
      type: Array,
      required: true,
      default () {
        return []
      }
    },
    previousSeatNames: {
      type: Array,
      required: true,
      default () {
        return []
      }
    },
    seatChartConfiguration: {
      type: Object,
      required: true,
      default () {
        return {}
      }
    },
    vertical: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    legendSeats () {
      let customSeatTypes = _.map(_.filter(_.flatten(this.seatChartArray), seat => seat.customSeatType), seatType => {
        return { name: seatType.customSeatTypeName, seatType: seatType.seatType, customSeatTypeColor: seatType.customSeatTypeColor }
      })
      customSeatTypes = _.uniqBy(customSeatTypes, seatType => [seatType.name, seatType.customSeatTypeColor].join())
      return _.filter(_.union(legendSeats, customSeatTypes), seat => {
        if (seat.required) return true

        if (seat.name === AVAILABILITY_TYPES.SOCIAL_DISTANCING) return this.seatChartConfiguration.socialDistancing

        if (seat.name === AVAILABILITY_TYPES.PRIOR_SEATS) {
          return this.previousSeatNames && this.previousSeatNames.length
        }

        return _.some(_.flatten(this.seatChartArray), someSeat => (someSeat.customSeatTypeName === seat.seatType) || (!someSeat.customSeatType && someSeat.seatType === seat.seatType))
      })
    },
    topLegendSeats () {
      return _.filter(legendSeats, seat => {
        if (seat.required) return true

        if (!seat.topLegend) return false

        if (seat.name === AVAILABILITY_TYPES.PRIOR_SEATS) {
          return this.previousSeatNames && this.previousSeatNames.length
        }

        return _.some(_.flatten(this.seatChartArray), someSeat => someSeat.seatType === seat.seatType)
      })
    },
    getLegendSeats () {
      return this.topLegend ? this.topLegendSeats : this.legendSeats
    }
  },
  created () {
    _.extend(this, { AVAILABILITY_TYPES })
  },
  methods: {
    showSeatIcon (seat) {
      return seat.reserved || (!seat.available && seat.name && seat.name !== AVAILABILITY_TYPES.AISLE && seat.name !== AVAILABILITY_TYPES.AVAILABLE)
    },
    seatIcon (seat) {
      if (seat.name === AVAILABILITY_TYPES.SELECTED) {
        return 'fas fa-check'
      } else if (seat.name === AVAILABILITY_TYPES.RESERVED) {
        return 'fas fa-user'
      } else if (seat.name === AVAILABILITY_TYPES.UNAVAILABLE) {
        return 'fas fa-user-slash'
      }
      return undefined
    },
    seatIconColor (seat) {
      if (seat.name === AVAILABILITY_TYPES.SELECTED) return this.$getColor(this.currentSite, 'seatSelected')
      if (seat.name === AVAILABILITY_TYPES.RESERVED || seat.name === AVAILABILITY_TYPES.SOCIAL_DISTANCING) return 'negative'
      return 'responsive-grey-5'
    }
  }
}

</script>
<style lang="scss" scoped>
.seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }

  .absolute-center{
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    transform: none;
  }
  .seat-swap-mode:not(.override) {
    pointer-events: none;
  }
  .seat-text {
    font-weight: 800;
    text-align: center;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    user-select: none;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .show-scrollbars {
    overflow: auto;
  }

  .social-distancing {
    opacity: 0.4
  }

  .previous-selection {
    border: dashed blue 2px;
    margin: -2px 0px -2px -2px;
    width: 24px;
  }

  .top-legend .previous-selection {
    width: 29px;
    height: 35px;
  }

  .legend .previous-selection {
    width: 38px;
    height: 44px;
  }

  .top-legend-seat-name {
    font-size: 1.5vh;
    font-weight: 500;
  }
</style>
